import { ref, Ref } from 'vue';
import { useNotify, NotifyType } from '@/composables/useNotify';
import { useI18n } from 'vue-i18n';
import { hubClient, getApiErrorMessage } from '@tfpl/api-client';
import UserVisionModule from '@/store/modules/UserVisionModule';
import { getConfig } from '@/config';

interface UseUser1on1 {
  loading: Ref<boolean>;
  error: Ref<Error>;
  showError: Ref<boolean>;
  delete1on1: (uuid: string) => Promise<void>;
  create1on1: (dto: hubClient.CreateCollaborator1On1Dto) => Promise<void>;
  update1on1: (uuid: string, dto: hubClient.UpdateCollaborator1On1Dto) => Promise<void>;
  validate1on1: (
    oneOnOneDraft: string,
    userRating: string | null | undefined,
    companyRating: string,
    isFinancialMessage: boolean,
  ) => Promise<hubClient.Validate1On1ResponseDto>;
}

export const useUser1on1: () => UseUser1on1 = () => {
  const { notify } = useNotify();
  const { t } = useI18n();
  const loading = ref(false);
  const error = ref();
  const showError = ref(false);
  const setError = (err: boolean): void => {
    showError.value = err;
  };

  const create1on1 = async (dto: hubClient.CreateCollaborator1On1Dto) => {
    try {
      loading.value = true;
      await hubClient.V3Collaborator1On1Service.createOneV3({
        path: {
          tenant: getConfig().TENANT,
          company: getConfig().TENANT,
        },
        body: dto,
        throwOnError: true,
      });
    } catch (e) {
      error.value = e;
      setError(true);
      notify(t('common.requestError'), NotifyType.ERROR);
      console.log('[error]', getApiErrorMessage(e) ?? e);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const update1on1 = async (uuid: string, dto: hubClient.UpdateCollaborator1On1Dto) => {
    try {
      loading.value = true;
      await hubClient.V3Collaborator1On1Service.updateOneV3({
        path: {
          tenant: getConfig().TENANT,
          company: getConfig().TENANT,
          uuid,
        },
        body: dto,
        throwOnError: true,
      });
    } catch (e) {
      error.value = e;
      setError(true);
      notify(t('common.requestError'), NotifyType.ERROR);
      console.log('[error]', getApiErrorMessage(e) ?? e);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const delete1on1 = async (uuid: string) => {
    try {
      loading.value = true;
      await hubClient.V3Collaborator1On1Service.deleteOneV3({
        path: {
          tenant: getConfig().TENANT,
          company: getConfig().TENANT,
          uuid,
        },
        throwOnError: true,
      });
    } catch (e) {
      error.value = e;
      setError(true);
      notify(t('common.requestError'), NotifyType.ERROR);
      console.log('[error]', getApiErrorMessage(e) ?? e);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const validate1on1 = async (
    oneOnOneDraft: string,
    userRating: string | null | undefined,
    companyRating: string,
    isFinancialMessage: boolean,
  ): Promise<hubClient.Validate1On1ResponseDto> => {
    const { data } = await hubClient.V3OpenAiService.validate1On1({
      path: {
        tenant: getConfig().TENANT,
        company: getConfig().TENANT,
      },
      body: {
        oneOnOneDraft: oneOnOneDraft,
        vision: UserVisionModule.userVision?.message ?? '',
        collaboratorRating: userRating ?? undefined,
        companyRating: companyRating,
        isFinancialMessage: isFinancialMessage,
      },
      throwOnError: true,
    });
    return data;
  };

  return {
    loading,
    error,
    showError,
    create1on1,
    update1on1,
    delete1on1,
    validate1on1,
  };
};
